import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { bandwidthQuery } from "./usage.queries";
import { sumBandwidth, sumProjectUsers } from "./usage.utils";
import { formatBytes, formatCount } from "../charts/charts.utils";
import StatCard from "../kpi/StatCard";

import type { JSX } from "react";

import type { AppState } from "@/src/reducers";

import type { UsagePeriodProps } from "./usage.types";

function UsageKpiGrid({ period }: UsagePeriodProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const bandwidthResult = useQuery(bandwidthQuery(period));
  const { activeUserCount, registeredUserCount } = useMemo(
    () => sumProjectUsers(projects.data),
    [projects.data],
  );
  const projectsQuery = {
    isLoading: !projects.isFetched && !projects.failed,
    isFetching: projects.isFetching,
    isError: projects.failed,
  };

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
      <StatCard
        label={t("activeUsers")}
        query={projectsQuery}
        scope="asOfToday"
        value={formatCount(activeUserCount, lang)}
      />
      <StatCard
        label={t("registeredUsers")}
        query={projectsQuery}
        scope="asOfToday"
        value={formatCount(registeredUserCount, lang)}
      />
      <StatCard
        label={t("editor-dashboard:activeContents")}
        query={projectsQuery}
        scope="asOfToday"
        value={formatCount(projects.data[0]?.contents ?? 0, lang)}
      />
      <StatCard
        label={t("bandwidth")}
        query={bandwidthResult}
        scope="period"
        value={formatBytes(sumBandwidth(bandwidthResult.data), lang)}
      />
    </div>
  );
}

export default UsageKpiGrid;
